import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { IEmbassyMessage, IEmbassyMessageListing } from '.';
import Loader from 'src/components/Loader';
import EditIcon from '@mui/icons-material/Edit';
import UpdateEmbassyMessage from './update-embassy-message';

const EmbassyMessageListing: React.FC = () => {
  const dispatch = useAppDispatch();

  const [data, setData] = useState<Record<string, IEmbassyMessageListing>>();
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openUpdateMessage, setOpenUpdateMessage] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<IEmbassyMessage | null>(null);

  const editMessage = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/EmbassyMessage/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const message: IEmbassyMessage = res.data;
        setSelectedMessage(message);
        setOpenUpdateMessage(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch embassy message details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading embassy message details', type: 'error' }));
    }
  }, [dispatch]);

  const headers = React.useMemo((): EnhancedTableColumn[] => [
    { id: 'title', label: 'Title', type: EnhancedTableColumnType.TEXT },
    { id: 'description', label: 'Description', type: EnhancedTableColumnType.TEXT },
    { id: 'language', label: 'Language', type: EnhancedTableColumnType.TEXT },
    { id: 'updatedAt', label: 'Updated At', type: EnhancedTableColumnType.TEXT },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: editMessage,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit Embassy Message'
        }
      ]
    }
  ], [editMessage]);

  const fetchEmbassyMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/EmbassyMessage/all');
      if (response.ok) {
        const res = await response.json();
        const list: IEmbassyMessage[] = res.data;
        const result: Record<string, IEmbassyMessageListing> = {};

        list.forEach((message) => {
          result[message.id] = {
            id: message.id,
            title: message.title,
            description: message.description.length > 100
              ? message.description.substring(0, 100) + '...'
              : message.description,
            language: message.language,
            updatedAt: new Date(message.updatedAt).toLocaleString()
          };
        });

        setData(result);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch embassy messages', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading embassy messages', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchEmbassyMessages();
  }, [fetchEmbassyMessages, refreshKey]);

  const handleUpdateSuccess = useCallback(() => {
    setOpenUpdateMessage(false);
    setSelectedMessage(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: 'Embassy message updated successfully!',
      type: 'success'
    }));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    setOpenUpdateMessage(false);
    setSelectedMessage(null);
  }, []);

  const actions: ITableMenuAction[] = [];

  if (!data || loading) {
    return (
      <Box>
        <Helmet>
          <title>Embassy Messages List</title>
        </Helmet>
        <MainPageTitle title={'Embassy Messages List'} />
        <Loader />
      </Box>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Embassy Messages List</title>
      </Helmet>
      <MainPageTitle title={'Embassy Messages List'} />

      <EnhancedTable
        title="Embassy Messages"
        columns={headers}
        data={data}
        actions={actions}
      />
      {openUpdateMessage && selectedMessage && (
        <UpdateEmbassyMessage
          onSuccess={handleUpdateSuccess}
          onClose={handleClose}
          message={selectedMessage}
        />
      )}
    </div>
  );
};

export default EmbassyMessageListing;
